import { onMounted, onUnmounted, ref, computed } from 'vue'
import InApp from 'detect-inapp'
import InAppSpy from 'inapp-spy'

export function useAppOpener() {
	const APP_LINK = 'buyandship://'
	const FALLBACK_LINK = 'https://qr.page/g/tSLYydLM2e'
	const PRODUCT_SHARING_APP_LINK_TEMPLATE =
		'buyandship://routing?screen=/product_sharing_post_detail#{post_id}/login'
	const PRODUCT_SHARING_WITH_CHECKOUT_APP_LINK_TEMPLATE =
		'buyandship://routing?screen=/product_sharing_post_detail#{post_id}/buyandshop_order_preview#{post_id}'
	const SHARING_POST_PATH = '/sharing/posts/'
	const TIMEOUT = 3000

	const hasOpened = ref<boolean>(false)
	const isProcessing = ref<boolean>(false)
	let timeoutId: ReturnType<typeof setTimeout> | null = null

	// in-app-solution
	const { isInApp } = InAppSpy()

	const isJPRegion = computed(
		() => window.BUYANDSHIP.settings.current_country === 'jp',
	)

	function isInAppBrowser() {
		const inapp = new InApp(
			navigator.userAgent || navigator.vendor || window.opera,
		)

		if (isInApp && inapp.isMobile) {
			return true
		}

		return false
	}

	function resetState() {
		hasOpened.value = false
		isProcessing.value = false
		if (timeoutId) {
			clearTimeout(timeoutId)
			timeoutId = null
		}
	}

	function getIdFromPath(pathname: string): string | null {
		const enPrefix = '/en'
		const fullSharingPostPath = `${enPrefix}${SHARING_POST_PATH}`

		if (pathname.startsWith(fullSharingPostPath)) {
			pathname = pathname.slice(enPrefix.length)
		}

		const trimmedPath = pathname.endsWith('/')
			? pathname.slice(0, -1)
			: pathname

		const id = trimmedPath.slice(SHARING_POST_PATH.length)

		return id.length > 0 && /^\w+$/.test(id) ? id : null
	}

	function isProductSharingPage(): boolean {
		return getIdFromPath(window.location.pathname) !== null
	}

	function getAppLink(withCheckout: boolean = false): string {
		if (isProductSharingPage()) {
			const id = getIdFromPath(window.location.pathname)
			if (id) {
				let appLink = withCheckout
					? PRODUCT_SHARING_WITH_CHECKOUT_APP_LINK_TEMPLATE
					: PRODUCT_SHARING_APP_LINK_TEMPLATE

				// 1. replace all {post_id}
				appLink = appLink.replace(/{post_id}/g, id)

				// 2. encoding after screen= value
				const parts = appLink.split('screen=')
				if (parts.length === 2) {
					const encodedScreen = encodeURIComponent(parts[1])
					appLink = `${parts[0]}screen=${encodedScreen}`
				}

				return appLink
			}
		}
		return APP_LINK
	}

	function openApp() {
		if (isProcessing.value) return
		openAppWithLink(getAppLink())
	}

	function openAppWithCheckout() {
		if (isProcessing.value) return
		openAppWithLink(getAppLink(true))
	}

	function openAppWithLink(appLink: string) {
		resetState()
		isProcessing.value = true

		window.location.href = appLink

		const startTime = Date.now()

		timeoutId = setTimeout(() => {
			if (!hasOpened.value && Date.now() - startTime < TIMEOUT + 100) {
				window.open(FALLBACK_LINK, '_self', 'noreferrer, noopener')
			}
			isProcessing.value = false
		}, TIMEOUT)
	}

	function handleVisibilityChange() {
		if (document.hidden) {
			hasOpened.value = true
			isProcessing.value = false
		}
	}

	onMounted(() => {
		document.addEventListener('visibilitychange', handleVisibilityChange)
	})

	onUnmounted(() => {
		document.removeEventListener('visibilitychange', handleVisibilityChange)
		resetState()
	})

	return {
		isJPRegion,
		isInAppBrowser,
		openApp,
		openAppWithCheckout,
	}
}
